import { get, post, put, del } from '@/services/http';
import { ReportModel } from '@/models/report';
import { MeasureModel } from '@/models/measure';
import { DimensionModel } from '@/models/dimension';
import { FilterColumnList } from '@/collections/filter_columns';
import { FilterColumnModel } from '@/models/filter_column';

interface SettingScope {
    product: string;
    location: string;
    parameter: string;
    supplier: string;
  }

// Helper to coerce various truthy representations to boolean
const toBoolean = (value: any): boolean => {
  if (value === null || value === undefined) {
    return false;
  }
  return value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true';
};

export class ReorderSettingParam {
  public replenishmentExclusion: boolean = false;
  public leadtime: number | null = null;
  public locationId: number = 0;
  public supplierId: number = 0;
  public orderCycle: number | null = null;
  public fixOrderQuantity: number | null = null;
  public minOrderQuantity: number | null = null;
  public maxOrderQuantity: number | null = null;
  public minOrderValue: number | null = null;
  public maxOrderValue: number | null = null;
  public minInventoryQuantity: number | null = null;
  public maxInventoryQuantity: number | null = null;
  public minStockCoverDays: number | null = null;
  public maxStockCoverDays: number | null = null;
  public caseQuantity: number | null = null;
  public scope: string = 'global';

  constructor(data: any = {}) {
      this.replenishmentExclusion = toBoolean(data.replenishmentExclusion);
      this.leadtime = data.leadtime ?? null;
      this.locationId = data.locationId ?? 0;
      this.supplierId = data.supplierId ?? 0;
      this.orderCycle = data.orderCycle ?? null;
      this.fixOrderQuantity = data.fixOrderQuantity ?? null;
      this.minOrderQuantity = data.minOrderQuantity ?? null;
      this.maxOrderQuantity = data.maxOrderQuantity ?? null;
      this.minOrderValue = data.minOrderValue ?? null;
      this.maxOrderValue = data.maxOrderValue ?? null;
      this.minInventoryQuantity = data.minInventoryQuantity ?? null;
      this.maxInventoryQuantity = data.maxInventoryQuantity ?? null;
      this.minStockCoverDays = data.minStockCoverDays ?? null;
      this.maxStockCoverDays = data.maxStockCoverDays ?? null;
      this.caseQuantity = data.caseQuantity ?? data['[case_quantity]'] ?? null;
      this.scope = data.scope ?? 'global';
  }

  public async saveReorder(filterColumns: FilterColumnList, setting: any, settingScope: SettingScope) {
    const report = new ReportModel();
    report.measure = new MeasureModel({code: 'replenishment_settings'});
    if (settingScope.parameter == 'specific') {
      if (settingScope.product == 'product') {
        report.dimension = new DimensionModel({code: 'by_product'});
      } else {
        report.dimension = new DimensionModel({code: 'by_variant'});
      }
    }
    if (settingScope.parameter == 'supplier') {
      report.dimension = new DimensionModel({code: 'supplier'});
    }
    if (settingScope.parameter == 'location') {
      report.dimension = new DimensionModel({code: 'by_location'});
    }
    report.isDetail = true;
    report.isMetric = false;
    report.page = 1;
    report.perPage = 100;
    report.filterColumns = filterColumns;
    // Generate report params
    const reportRequestParams = report.generateParams();

    // Helper function to conditionally add a field to the data object
    // Guards against null, undefined, empty strings, and NaN values
    const addIfNotNull = (obj: any, key: string, value: number | null | string) => {
      if (value === null || value === undefined || value === '') {
        return;
      }
      const numericValue = Number(value);
      if (Number.isNaN(numericValue)) {
        return;
      }
      obj[key] = numericValue;
    };

    // Build base data object with required fields
    const data: any = {
      options: reportRequestParams,
      replenishment_exclusion: this.replenishmentExclusion ? 1 : 0,
      setting,
      scope: this.scope,
    };

    // Conditionally add numeric fields only if they have values
    addIfNotNull(data, 'leadtime', this.leadtime);
    addIfNotNull(data, 'order_cycle', this.orderCycle);
    addIfNotNull(data, 'fix_order_quantity', this.fixOrderQuantity);
    addIfNotNull(data, 'min_order_quantity', this.minOrderQuantity);
    addIfNotNull(data, 'max_order_quantity', this.maxOrderQuantity);
    addIfNotNull(data, 'min_order_value', this.minOrderValue);
    addIfNotNull(data, 'max_order_value', this.maxOrderValue);
    addIfNotNull(data, 'min_inventory_quantity', this.minInventoryQuantity);
    addIfNotNull(data, 'max_inventory_quantity', this.maxInventoryQuantity);
    addIfNotNull(data, 'min_stock_cover_days', this.minStockCoverDays);
    addIfNotNull(data, 'max_stock_cover_days', this.maxStockCoverDays);

    // Add supplier_id if set
    if (this.supplierId > 0) {
      data.supplier_id = this.supplierId;
    }

    // Add location_id if set
    if (this.locationId > 0) {
      data.location_id = this.locationId;
    }

    // Build extra_configs for additional parameters
    const extraConfigs: any = {};
    if (this.caseQuantity !== null && this.caseQuantity !== undefined) {
      // Guard against NaN/Infinity even though type is number (defensive coding)
      if (!Number.isNaN(this.caseQuantity) && Number.isFinite(this.caseQuantity)) {
        extraConfigs.case_quantity = this.caseQuantity;
      }
    }
    if (Object.keys(extraConfigs).length > 0) {
      data.extra_configs = JSON.stringify(extraConfigs);
    }

    const res: any = await put('/replenishment_settings', data);
  }

  /**
   * Resets specified fields in the replenishment settings
   * @param fields - Array of field names to reset
   * @param reportRequestParams - Report parameters to identify which records to reset
   * @returns Promise that resolves when the reset is complete
   */
  public async reset(fields: any, reportRequestParams: any) {
    const data = {
      options: reportRequestParams,
      fields,
    };
    const res: any = await del('/replenishment_settings', data);
  }

  public mapData(data: any = {}) {
    // Helper to get value from either camelCase or bracket notation, preserving null/0
    const getValue = (camelKey: string, bracketKey: string): any => {
      if (data[camelKey] !== undefined && data[camelKey] !== null) {
        return data[camelKey];
      }
      if (data[bracketKey] !== undefined && data[bracketKey] !== null) {
        return data[bracketKey];
      }
      return null;
    };

    this.replenishmentExclusion = toBoolean(getValue('replenishmentExclusion', '[replenishment_exclusion]'));
    this.leadtime = getValue('leadtime', '[vendor_lead_time]');
    this.orderCycle = getValue('orderCycle', '[reorder_cycle]');
    this.fixOrderQuantity = getValue('fixOrderQuantity', '[fixed_order_quantity]');
    this.minOrderQuantity = getValue('minOrderQuantity', '[min_order_quantity]');
    this.maxOrderQuantity = getValue('maxOrderQuantity', '[max_order_quantity]');
    this.minOrderValue = getValue('minOrderValue', '[min_order_value]');
    this.maxOrderValue = getValue('maxOrderValue', '[max_order_value]');
    this.minInventoryQuantity = getValue('minInventoryQuantity', '[min_inventory_quantity]');
    this.maxInventoryQuantity = getValue('maxInventoryQuantity', '[max_inventory_quantity]');
    this.minStockCoverDays = getValue('minStockCoverDays', '[min_stock_cover_days]');
    this.maxStockCoverDays = getValue('maxStockCoverDays', '[max_stock_cover_days]');
    this.caseQuantity = getValue('caseQuantity', '[case_quantity]');
    this.scope = getValue('scope', '[scope]') ?? 'global';
    this.supplierId = getValue('supplierId', '[supplier_id]') ?? 0;
    this.locationId = getValue('locationId', '[location_id]') ?? 0;
  }

  public async getGlobalParams(supplierScope: string = 'global') {
    try {
      const report = new ReportModel();
      // Set up measure
      report.measure = new MeasureModel({ code: 'replenishment_settings' });
      // Set report type
      report.isDetail = true;
      report.isMetric = false;
      report.page = 1;
      report.perPage = 100;

      const filterColumns = new FilterColumnList();
      // Add initial filter columns
      const filterColumn = new FilterColumnModel();
      if (supplierScope === 'specific') {
        filterColumn.code = 'supplier_id';
        filterColumn.value = [this.supplierId.toString()];
        filterColumn.operator = 'equal';
        filterColumn.dataType = 'number';
        filterColumns.add(filterColumn);
        report.filterColumns = filterColumns;
      }
      // Get report data
      await report.getReport(false, false);

      const rows: any = report.getReportRowsData();
      if (rows.length > 0) {
        this.mapData(rows[0]);
      }
    } catch (error) {
      console.error('Error fetching supplier products:', error);
      throw error;
    }
  }
}
